import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useDispatch, useSelector } from 'react-redux';
import { setAlert } from '../../redux/dashboard/actions';
import { getSelectedProject } from '../../redux/projects/actions';
import ToolBar from '../../components/toolbar';
import Table from '../../components/table';
import tableHeaders from './tableHeader';
import SidePanel from '../../components/sidepanel';
import MultipleFilters from '../../components/multipleFilters';
import Wizard from '../../components/wizard';
import siteSteps from './siteSteps';
import ManageSitesModal from './manageSitesModal';

import filterUtils from '../../utils/filters';
import utils from '../../utils';
import './styles.scss';
import API from '../../services/api';
import EmptyState from '../../components/emptyState';
import MultipleSitesCreationModal from './multipleSitesCreation';
import CustomConfirm from '../../components/customConfirm';
import { getUser } from '../../redux/user/actions';

const SitesContainer = ({ lastUpdated }) => {
  const [data, setData] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [totalSize, setTotalSize] = useState(0);

  const [filterBy, setFilterBy] = useState([]);
  const [searchBy, setSearchBy] = useState('');
  const [itemsCount, setItemsCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [newSite, setNewSite] = useState({});
  const [lastFetchTime, setLastFetchTime] = useState(lastUpdated);

  // Flags
  const [isLoading, setIsLoading] = useState(true);
  const [createMode, setCreateMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMultipleCreationModal, setShowMultipleCreationModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const siteGroup = useSelector(getSelectedProject);

  const nextPageToken = useRef('');
  const filters = useRef('');
  const sorting = useRef('displayName, id');
  const sites = useRef([]);
  const assetsBySiteId = useRef({});
  const assetsProjectId = useRef('');
  const oldSiteGroupId = useRef('');
  const oldLastFetchTime = useRef(lastUpdated);
  const allSitesFetched = useRef(false);
  const user = useSelector(getUser);

  const exportSitesCSV = () => {
    const headers = [
      'display_name',
      'type',
      'description',
      'solution',
      'address',
      'latitude',
      'longitude',
      'country_code'
    ];

    const rows = sites.current.map(site => {
      const solutions = [];

      if (site.datasources.find(ds => ds == 'MOBILE_M')) solutions.push('mobile');
      if (site.datasources.find(ds => ds == 'AUDIENCE_M')) solutions.push('audience');
      if (site.datasources.find(ds => ds == 'TRAFFIC_M')) solutions.push('traffic');

      return [
        '"' + site.displayName + '"',
        site.type.stdName,
        '"' + site.description + '"',
        solutions.join('_'),
        '"' + site.address + '"',
        site.latitude,
        site.longitude,
        site.countryCode
      ];
    });

    const csvContent = [headers]
      .concat(rows)
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${siteGroup.displayName}-sites.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toolBarButtons = [
    {
      title: 'Create Site',
      classes: 'btn-primary',
      handleClick: () => setCreateMode(true)
    },
    {
      title: 'Create Multiple Sites',
      classes: 'btn-secondary',
      handleClick: () => setShowMultipleCreationModal(true)
    }
  ];

  if (user && user.admin) {
    toolBarButtons.push({
      title: 'Export CSV',
      classes: 'btn-secondary',
      handleClick: () => exportSitesCSV()
    });
  }

  const dispatch = useDispatch();

  const attachAssetsToSites = siteList =>
    siteList.map(site => ({
      ...site,
      assets: assetsBySiteId.current[site.id] || []
    }));

  useEffect(() => {
    setLastFetchTime(lastUpdated);
  }, [lastUpdated]);

  useEffect(() => {
    if (!siteGroup) return undefined;

    let active = true;
    const projectId = siteGroup.id;
    assetsProjectId.current = projectId;
    assetsBySiteId.current = {};

    API.getAllAssets(projectId)
      .then(response => {
        if (!active || assetsProjectId.current !== projectId) return;

        assetsBySiteId.current = (response.assets || []).reduce((assetsBySite, asset) => {
          if (!assetsBySite[asset.siteId]) assetsBySite[asset.siteId] = [];
          assetsBySite[asset.siteId].push(asset);
          return assetsBySite;
        }, {});

        sites.current = attachAssetsToSites(sites.current);
        setData(currentData => attachAssetsToSites(currentData));
      })
      .catch(error => {
        if (active) console.error('Could not load Project Assets.', error);
      });

    return () => {
      active = false;
    };
  }, [siteGroup, lastFetchTime]);

  useEffect(() => {
    if (sites.current.length === 0) {
      fetchData({ pageIndex: 0, pageSize: 15, sortBy: [] });
    }
  }, [siteGroup, filterBy, searchBy]);

  const getFilters = () => {
    let newFilters = [];

    if (searchBy !== '') {
      newFilters.push(`(displayName ILIKE "%${searchBy}%" OR id ILIKE "%${searchBy}%")`);
    }

    filterBy.forEach(filter => {
      const operator = filter.operator ? filter.operator : '=';
      const value = filter.type === 'version' ? `"${filter.value}"` : filter.value;
      newFilters.push(`(${filter.path} ${operator} ${value})`);
    });

    return newFilters.join(' AND ');
  };

  const handleSearch = useDebouncedCallback(term => {
    setSearchBy(term);
  }, 300);

  const getSorting = sortBy => {
    if (sortBy.length > 0 && sortBy[0].id) {
      return `${sortBy[0].id} ${sortBy[0].desc ? 'DESC' : 'ASC'}, id`;
    }

    return (sorting.current = 'displayName, id');
  };

  const getSiteList = (newSites, forceUpdate) => {
    // Merge new data with previous data if sorting and filters hasn't change
    if (!forceUpdate) {
      return attachAssetsToSites([...sites.current, ...newSites]);
    }

    // Overwrite list with new data
    return attachAssetsToSites(newSites);
  };

  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      if (!siteGroup) {
        return;
      }
      // Keep old sorting and filters to handle changes
      const oldSorting = sorting.current;
      const oldFilters = filters.current;

      // Apply Sorting and filters
      filters.current = encodeURI(getFilters());
      sorting.current = getSorting(sortBy);

      const forceUpdate =
        oldSorting !== sorting.current ||
        oldFilters !== filters.current ||
        lastFetchTime !== oldLastFetchTime.current ||
        siteGroup.id !== oldSiteGroupId.current;

      // Clear pageToken if project, filter or sorting has changed
      if (forceUpdate) {
        nextPageToken.current = '';
        setIsLoading(true);
        allSitesFetched.current = false;
      }

      if (forceUpdate || !allSitesFetched.current) {
        API.getSites(siteGroup.id, nextPageToken.current, filters.current, sorting.current).then(response => {
          // Update site list
          sites.current = getSiteList(response.sites, forceUpdate);

          if (response.nextPageToken === '') {
            allSitesFetched.current = true;
          }

          nextPageToken.current = response.nextPageToken;
          oldSiteGroupId.current = siteGroup.id;

          // Compute total number of items
          setItemsCount(response.totalSize);

          // Update item count when there is no filters applied
          if (filters.current === '') {
            setTotalSize(response.totalSize);
          }

          // Update table indexes
          const startRow = pageSize * pageIndex;
          const endRow = startRow + pageSize;

          // Slice items from device list
          setData(sites.current.slice(startRow, endRow));

          // Compute total page count
          setPageCount(Math.ceil(response.totalSize / pageSize));
          setIsLoading(false);
        });
      } else {
        // Update table indexes
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;

        // Slice items from device list
        setData(sites.current.slice(startRow, endRow));
        setIsLoading(false);
      }
    },
    [searchBy, filterBy, siteGroup, lastFetchTime]
  );

  const onCreate = async () => {
    const {
      address,
      countryCode,
      datasources,
      description,
      displayName,
      geometry,
      latitude,
      longitude,
      solution,
      type,
      assets = []
    } = newSite;

    const siteApiObject = {
      address,
      countryCode,
      datasources,
      description,
      displayName,
      ...(geometry ? { geometry } : {}),
      latitude,
      longitude,
      projectId: siteGroup.id,
      solution,
      status: 'ENABLED',
      type
    };

    try {
      const createdSite = await API.createSite(siteApiObject);

      await Promise.all(
        assets.map(async asset => {
          const attachedDeviceIds = asset.attachedDeviceIds || [];
          const createdAsset = await API.createAsset(siteGroup.id, createdSite.id, {
            attachedDeviceIds: asset.attachedDeviceIds || [],
            displayName: asset.displayName,
            siteId: createdSite.id,
            type: asset.type || 'UNKNOWN'
          });

          const devicesToMove = (asset.attachedDevices || [])
            .filter(device => device.requiresDetach)
            .map(device => device.id);
          if (devicesToMove.length > 0) {
            await API.detachAssetDevicesFromOtherSites(
              siteGroup.id,
              createdSite.id,
              createdAsset.id,
              devicesToMove
            );
          }

          if (attachedDeviceIds.length > 0) {
            await API.updateAssetAttachedDevices(
              siteGroup.id,
              createdSite.id,
              createdAsset.id,
              attachedDeviceIds
            );
          }
        })
      );

      dispatch(setAlert(utils.generateAlert('Site created successfully!', 'success')));
    } catch (error) {
      dispatch(setAlert(utils.generateAlert('Site creation could not be completed. Please try again.', 'error')));
      console.error(error);
    } finally {
      setLastFetchTime(new Date().toISOString());
      fetchData({ pageIndex: 0, pageSize: 15, sortBy: [] });
      setCreateMode(false);
      setNewSite({});
    }
  };

  const handleSiteSelection = site => {
    setSelectedSite(site);
    setShowModal(true);
  };

  const handleSiteEdition = () => {
    const newSites = utils.deepClone(sites);
    const siteIndex = newSites.findIndex(site => site.id === selectedSite.id);
    newSites.splice(siteIndex, 1, selectedSite);
    sites.current = newSites;
  };

  if (createMode) {
    return (
      <div className="sites site-creation">
        <Wizard
          title={'Create a new site'}
          icon={'location'}
          steps={siteSteps(newSite, setNewSite, siteGroup.id, true)}
          onSubmit={onCreate}
          onCancel={() => {
            setCreateMode(false);
            setNewSite({});
          }}
        />
      </div>
    );
  }

  return (
    <div className="sites d-flex flex-column h-100">
      <ToolBar
        title="Sites"
        icon="location"
        searchPlaceholder={'Search site'}
        count={totalSize > 0 ? totalSize : null}
        onSearch={handleSearch}
        searchingBy={searchBy}
        hasMultipleButtons
        buttonsList={toolBarButtons}
      />
      <div className="sites-container d-flex">
        <SidePanel
          content={
            <MultipleFilters
              items={[]}
              filters={filterUtils.getFilters(['Site Type', 'Site Solution', 'Site Status'], false)}
              filterBy={filterBy}
              onFilter={(_filteredItems, appliedFilters) => {
                setFilterBy(appliedFilters);
              }}
            />
          }
          isOpen={showFilters}
        />
        <div className={`d-flex flex-fill  ${!showModal || showFilters ? 'split-view' : ''}`}>
          {sites.current.length === 0 ? (
            <div className="d-flex h-100 w-100 align-items-center justify-content-center">
              <EmptyState
                icon={'location'}
                title={'No Sites found. Create your first site.'}
                details={'If you do have created sites, please contact support.'}
              />
            </div>
          ) : (
            <Table
              columns={tableHeaders}
              data={data}
              pageCount={pageCount}
              itemsCount={itemsCount}
              fetchData={fetchData}
              searchBy={searchBy}
              filterBy={filterBy}
              loading={isLoading}
              isItemDetailsClosed={showModal}
              resetOnChange={siteGroup} // reset table when siteGroup change
              onItemSelected={site => {
                handleSiteSelection(site);
                setShowFilters(false);
              }}
              dataLabel={'sites'}
            />
          )}
        </div>
        {selectedSite && (
          <>
            <ManageSitesModal
              title={selectedSite.displayName}
              isOpen={showModal}
              toggle={keepOpen => {
                if (!keepOpen) {
                  setShowCancelDialog(false);
                  setShowModal(false);
                  setSelectedSite(null);
                  setLastFetchTime(new Date().toISOString());
                }
              }}
              onCancel={() => setShowCancelDialog(true)}
              onConfirm={handleSiteEdition}
              site={selectedSite}
              projectId={siteGroup.id}
            />
            <CustomConfirm
              messageType={'noModifications'}
              confirmCb={() => {
                setShowCancelDialog(false);
                setShowModal(false);
                setSelectedSite(null);
              }}
              cancelCb={() => {
                setShowCancelDialog(false);
              }}
              isOpen={showCancelDialog}
            />
            ;
          </>
        )}
        <MultipleSitesCreationModal
          isOpen={showMultipleCreationModal}
          toggle={() => setShowMultipleCreationModal(false)}
        />
      </div>
    </div>
  );
};

export default SitesContainer;
